import { Box, Button } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { useEffect, useState } from 'react'
import NoticeBanner from './NoticeBanner'
import type { Notice, NoticeSeverity } from './types'

type NoticesRegionProps = { notices: Notice[]; maxVisible?: number; sx?: SxProps<Theme> }

const SEVERITY_RANK: Record<NoticeSeverity, number> = { error: 0, warning: 1 }

const listSx = { listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }

const NoticesRegion = ({ notices, maxVisible = 3, sx }: NoticesRegionProps) => {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  // Dropping the id once its notice is gone keeps it collapsed if it comes back (e.g. tab round trip).
  useEffect(() => {
    if (expandedId && !notices.some(notice => notice.id === expandedId)) setExpandedId(null)
  }, [notices, expandedId])

  if (!notices.length) return null

  const sorted = [...notices].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity])
  const hiddenCount = sorted.length - maxVisible
  const visible = showAll || hiddenCount <= 0 ? sorted : sorted.slice(0, maxVisible)

  return (
    <Box aria-label="Avisos" component="section" sx={sx}>
      <Box component="ul" sx={listSx}>
        {visible.map(notice => (
          <li key={notice.id}>
            <NoticeBanner
              expanded={expandedId === notice.id}
              notice={notice}
              onToggle={() => setExpandedId(current => (current === notice.id ? null : notice.id))}
            />
          </li>
        ))}
        {hiddenCount > 0 && (
          <li>
            <Button
              aria-expanded={showAll}
              color="inherit"
              size="small"
              sx={{ py: 0 }}
              onClick={() => setShowAll(value => !value)}
            >
              {showAll
                ? 'Mostrar menos'
                : `Ver ${hiddenCount} ${hiddenCount === 1 ? 'aviso más' : 'avisos más'}`}
            </Button>
          </li>
        )}
      </Box>
    </Box>
  )
}

export default NoticesRegion
